import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Calendar, BarChart3 } from 'lucide-react';
import { TeamCapacityPanel } from '../PMO/TeamCapacityPanel';
import { ResourceHeatmapComponent } from '../../CommandCenter/ResourceHeatmap';
import { ResourceCalendar } from '../ProjectManagement/ResourceCalendar';
import { ResourceHeatmap } from '../../../models/CommandCenter';

interface UnifiedResourceManagementProps {
  project?: any;
  tasks?: any[];
  onResourceReallocation?: (taskId: string, newAssignee: string) => void;
  onCapacityAdjustment?: (userId: string, newCapacity: number) => void;
}

export const UnifiedResourceManagement: React.FC<UnifiedResourceManagementProps> = ({
  project,
  tasks,
  onResourceReallocation,
  onCapacityAdjustment
}) => {
  const [activeView, setActiveView] = useState<'team' | 'heatmap' | 'calendar'>('team');

  const mockHeatmapData: ResourceHeatmap[] = [
    {
      id: 'role-pm',
      name: 'Project Manager',
      utilization: 85,
      capacity: 40,
      status: 'busy' as const,
      role: 'Project Manager',
      team_members: 2,
      billable_hours: 32,
      total_hours: 38
    },
    {
      id: 'role-dev',
      name: 'Senior Developer',
      utilization: 95,
      capacity: 40,
      status: 'overloaded' as const,
      role: 'Senior Developer',
      team_members: 3,
      billable_hours: 36,
      total_hours: 40
    },
    {
      id: 'role-designer',
      name: 'UI/UX Designer',
      utilization: 65,
      capacity: 40,
      status: 'available' as const,
      role: 'UI/UX Designer',
      team_members: 1,
      billable_hours: 24,
      total_hours: 28
    }
  ];

  const views = [
    { id: 'team', label: 'Team Capacity', icon: Users },
    { id: 'heatmap', label: 'Resource Heatmap', icon: BarChart3 },
    { id: 'calendar', label: 'Resource Calendar', icon: Calendar }
  ];

  return (
    <div className="h-full p-8">
      <div className="mb-8">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4 drop-shadow-sm">
          Resource Management
        </h2>
        <p className="text-gray-600 text-lg drop-shadow-sm">
          Comprehensive team capacity, utilization heatmaps, and resource booking
        </p>
      </div>

      <div className="flex items-center space-x-1 bg-white border border-gray-200 rounded-lg p-1 mb-8 w-fit">
        {views.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveView(id as any)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 drop-shadow-sm ${
              activeView === id 
                ? 'bg-gray-900 text-white' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Icon className="w-4 h-4" />
            <span className="text-sm font-medium">{label}</span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeView}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="h-full"
        >
          {activeView === 'team' && (
            <div className="tyler-card overflow-hidden">
              <TeamCapacityPanel />
            </div>
          )}
          
          {activeView === 'heatmap' && (
            <div className="tyler-card p-6">
              <ResourceHeatmapComponent heatmap={mockHeatmapData} />
            </div>
          )}
          
          {activeView === 'calendar' && (
            <div className="tyler-card overflow-hidden">
              <ResourceCalendar 
                projectId={project?.id || 'proj-001'}
                viewMode="project"
                className="w-full"
              />
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
